<template>
  <v-container>
    <h2>LLM Evaluation - Technical Issue Flag</h2>
    <v-row v-if="showInput">
      <v-col cols="12" md="10">
        <v-textarea
          v-model="rawInput"
          label="Input (one per line: callId, label[0/1])"
          hint="Example: F270807204404V1U2300061,1"
          auto-grow
          rows="8"
        />
      </v-col>
      <v-col cols="12" md="2" class="d-flex align-end">
        <v-btn color="primary" @click="loadRows">Load</v-btn>
        <v-btn text class="ml-2" @click="clearAll">Clear</v-btn>
      </v-col>
    </v-row>

    <v-row v-else class="mb-4">
      <v-col cols="12">
        <v-btn text small @click="showInput = true">Edit Input</v-btn>
      </v-col>
    </v-row>

    <v-divider class="my-4"/>

    <v-row v-if="rows.length">
      <v-col cols="12">
        <div class="d-flex justify-space-between align-center mb-4">
          <h3>Evaluation Results</h3>
          <v-btn 
            color="secondary" 
            :loading="runningAll" 
            :disabled="allCompleted"
            @click="runAll"
          >
            {{ allCompleted ? 'All Complete' : 'Run All' }}
          </v-btn>
        </div>
        <v-data-table :items="rows" :headers="headers" dense :items-per-page="10">
          <template v-slot:item.actions="{ item }">
            <v-btn small color="primary" :loading="item.loading" @click="runRow(item)">Run</v-btn>
          </template>
          <template v-slot:item.correct="{ item }">
            <v-chip v-if="item.correct === true" color="green" dark small>Correct</v-chip>
            <v-chip v-else-if="item.correct === false" color="red" dark small>Incorrect</v-chip>
            <v-chip v-else color="grey" dark small>N/A</v-chip>
          </template>
          <template v-slot:item.responseText="{ item }">
            <pre style="white-space: pre-wrap; font-size: 11px; max-height: 160px; overflow: auto;">{{ item.responseText }}</pre>
          </template>
        </v-data-table>
      </v-col>
    </v-row>

    <v-row v-if="summary">
      <v-col cols="12" md="6">
        <h3>Summary</h3>
        <v-list dense>
          <v-list-item><v-list-item-content>Total: {{ summary.total }}</v-list-item-content></v-list-item>
          <v-list-item><v-list-item-content>Accuracy: {{ (summary.accuracy * 100).toFixed(1) }}%</v-list-item-content></v-list-item>
          <v-list-item><v-list-item-content>Precision: {{ (summary.precision * 100).toFixed(1) }}%</v-list-item-content></v-list-item>
          <v-list-item><v-list-item-content>Recall: {{ (summary.recall * 100).toFixed(1) }}%</v-list-item-content></v-list-item>
          <v-list-item><v-list-item-content>TP: {{ summary.tp }}, FP: {{ summary.fp }}, TN: {{ summary.tn }}, FN: {{ summary.fn }}</v-list-item-content></v-list-item>
        </v-list>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import db from '@/firebase/init'

export default {
  name: 'CallAnalysisEvaluation',
  data() {
    return {
      rawInput: '',
      showInput: true,
      rows: [],
      summary: null,
      runningAll: false,
      headers: [
        { text: 'Actions', value: 'actions', sortable: false },
        { text: 'Call ID', value: 'callId' },
        { text: 'Label', value: 'label' },
        { text: 'Prediction', value: 'prediction' },
        { text: 'Correct', value: 'correct' },
        { text: 'Response', value: 'responseText' }
      ]
    }
  },
  computed: {
    allCompleted() {
      return this.rows.length > 0 && this.rows.every(row => row.correct !== null)
    }
  },
  methods: {
    clearAll() {
      this.rawInput = ''
      this.rows = []
      this.summary = null
      this.showInput = true
    },
    parseInput() {
      const lines = (this.rawInput || '').split(/\n+/).map(l => l.trim()).filter(Boolean)
      return lines.map(line => {
        const parts = line.split(/[\,\s]+/)
        const callId = parts[0]
        const label = parts.length > 1 ? (parts[1] === '1' || parts[1].toLowerCase() === 'true') : null
        return { callId, label }
      }).filter(x => x.callId && x.label !== null)
    },
    loadRows() {
      const items = this.parseInput()
      this.rows = items.map(x => ({
        callId: x.callId,
        label: !!x.label,
        prediction: null,
        correct: null,
        responseText: 'Pending',
        loading: false
      }))
      this.showInput = false
      this.summary = null
    },
    computeSummary(rows) {
      let tp = 0, fp = 0, tn = 0, fn = 0
      rows.forEach(r => {
        if (r.correct === null) return
        if (r.prediction === true && r.label === true) tp++
        else if (r.prediction === true && r.label === false) fp++
        else if (r.prediction === false && r.label === false) tn++
        else if (r.prediction === false && r.label === true) fn++
      })
      const total = tp + fp + tn + fn
      const accuracy = total ? (tp + tn) / total : 0
      const precision = (tp + fp) ? tp / (tp + fp) : 0
      const recall = (tp + fn) ? tp / (tp + fn) : 0
      return { total, accuracy, precision, recall, tp, fp, tn, fn }
    },
    async runRow(item) {
      try {
        item.loading = true
        // Call the evaluation endpoint (LLM-only)
        const host = window.location.origin
        // Firebase callable route under /evaluate_callanalysis_satisfaction
        const url = `https://northamerica-northeast1-hydro-ottawa-ai.cloudfunctions.net/evaluate_callanalysis_satisfaction`
        const resp = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ callId: item.callId })
        })
        const data = await resp.json()
        if (!data.success) {
          item.prediction = null
          item.correct = null
          item.responseText = data.error || 'Error'
        } else {
          const result = data.result || {}
          // Use agent tech issue as the prediction by default; adjust as needed
          const prediction = !!result.technical_issue_agent
          item.prediction = prediction
          item.correct = prediction === !!item.label
          // show the full JSON response from the model
          item.responseText = JSON.stringify(result, null, 2)
        }
        // Update summary excluding N/A rows
        this.summary = this.computeSummary(this.rows)
      } catch (e) {
        item.prediction = null
        item.correct = null
        item.responseText = `Request failed: ${e.message}`
      } finally {
        item.loading = false
      }
    },
    async runAll() {
      this.runningAll = true
      try {
        // Run all rows that haven't been completed yet
        const pendingRows = this.rows.filter(row => row.correct === null)
        
        // Process in batches of 3 to avoid overwhelming the API
        const batchSize = 3
        for (let i = 0; i < pendingRows.length; i += batchSize) {
          const batch = pendingRows.slice(i, i + batchSize)
          
          // Run batch in parallel
          await Promise.allSettled(
            batch.map(row => this.runRow(row))
          )
          
          // Small delay between batches to be gentle on the API
          if (i + batchSize < pendingRows.length) {
            await new Promise(resolve => setTimeout(resolve, 1000))
          }
        }
      } finally {
        this.runningAll = false
      }
    }
  }
}
</script>

